import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

/**
 * Badge / Chip — usato per stati bolletta, contatori, label.
 * Mappa BOLLETTA → variant:
 *   pagata        → success
 *   da pagare     → secondary
 *   in scadenza   → warning
 *   scaduta       → destructive
 *   rateizzata    → tertiary
 *   stornata      → outline
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'bg-primary/10 text-primary',
        secondary: 'bg-secondary/15 text-secondary',
        tertiary: 'bg-tertiary/20 text-tertiary-foreground',
        success: 'bg-success/15 text-success',
        warning: 'bg-warning/20 text-warning-foreground',
        destructive: 'bg-destructive/12 text-destructive',
        outline: 'border border-border text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />
}

/**
 * Mappa stato bolletta → variant Badge (allineato al mobile).
 */
export type InvoiceStatus =
  | 'pagata'
  | 'da pagare'
  | 'in scadenza'
  | 'scaduta'
  | 'rateizzata'
  | 'stornata'

export const INVOICE_STATUS_VARIANT: Record<InvoiceStatus, BadgeProps['variant']> = {
  pagata: 'success',
  'da pagare': 'secondary',
  'in scadenza': 'warning',
  scaduta: 'destructive',
  rateizzata: 'tertiary',
  stornata: 'outline',
}

export function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  return (
    <Badge variant={INVOICE_STATUS_VARIANT[status]} className="capitalize">
      {status}
    </Badge>
  )
}
